<template>
  <main>
    <!-- START - STORE LOCATIONS HEADER -->
    <WCBreadcrumb
      :crumbs="storeCrumbs"
      @selected="breadcrumbs[breadcrumbs.length - 1]"
      class="p-0 ml-n2"
    />
    <div class="row justify-content-between align-items-center select-store-header">
      <h1 tabindex="-1" ref="focusFirst" class="col-sm-9">{{ $t('storeLocations') }}</h1>
      <!-- START : Search by zipcode input -->
      <section
        class="store-select store-select__section store-select-zipcode col-sm-3"
        :hidden="!map"
      >
        <label for="zipcode-search" class="font-weight-bold mb-2">{{
          $t('searchByZipcode')
        }}</label>
        <!-- START : Search Input component -->
        <WCSearchInput
          id="zipcode-search"
          :ariaLabel="$t('zipCode').toLowerCase()"
          :placeHolderText="$t('zipCode').toLowerCase()"
          @focusIn="hasFocus = true"
          @focusOut="hasFocus = false"
          :hasFocusInput="hasFocus"
          @updateValue="updateSearchValue"
          @onClear="updateSearchValue"
          @onSearch="search()"
          @onKeyDown="search()"
        />
        <!-- END : Search Input component -->
        <div aria-live="assertive">
          <WCValidationMessage class="mt-2" v-if="searchString && $v.searchString.$invalid">
            {{ $t('zipCodeInvalid') }}
          </WCValidationMessage>
          <WCValidationMessage
            v-else-if="geocodeError && geocodeError.search === searchString"
            class="mt-2"
          >
            {{ geocodeError.message }}
          </WCValidationMessage>
          <span class="sr-only" v-if="searchString && !geocodeError && !$v.searchString.$invalid">{{
            $t('zipCodeValid')
          }}</span>
        </div>
      </section>
      <!-- END : Search by zipcode input -->
    </div>
    <!-- END - STORE LOCATIONS HEADER -->

    <!-- START - STORE LOCATIONS SECTION - MAP & STORE INFO -->
    <WCSpinner v-if="loading" size="xl" position="center" />
    <div class="py-3" v-else>
      <section class="row store-select store-select__section">
        <div class="col-lg-5 wc-map-wrapper py-3" :hidden="!map">
          <div ref="mapElement" class="store-select store-select__map"></div>
        </div>

        <div class="col-lg-7 wc-store-list-wrapper py-3">
          <p v-if="!stores || !stores.length">{{ $t('noStoresFound') }}</p>
          <div v-if="stores && stores.length" class="store-select store-select__store-list">
            <WCStore
              v-for="store in storeList"
              :key="store.id"
              :store="store"
              :isHomeStore="isHomeStore(store)"
              :isCurrentStore="isCurrentStore(store)"
              :isSelected="isSelected(store)"
              @selectStore="selectStore"
            />
          </div>

          <WCPagination
            v-if="stores.length > pageSize"
            :collectionSize="stores.length"
            v-model="currentPage"
            :pageSize="pageSize"
            :maxSize="6"
          >
          </WCPagination>
        </div>
      </section>
    </div>
    <!-- END - STORE LOCATIONS SECTION - MAP & STORE INFO -->
  </main>
</template>

<script>
/* eslint-disable no-undef */
import axios from 'axios';
import { mapGetters } from 'vuex';
import FocusMixin from '@/mixins/FocusMixin';
import WCValidationMessage from '@/modules/forms/components/WCValidationMessage/WCValidationMessage.vue';
import { postal } from '@/utils/validators/validators';
import { maxLength } from 'vuelidate/lib/validators';
import MapService from '@/services/map.service';
import WCStore from '@/components/WCStore/WCStore.vue';
import WCPagination from '@/components/WCPagination/WCPagination.vue';
import WCSpinner from '@/components/WCSpinner/WCSpinner.vue';
import WCBreadcrumb from '@/components/WCBreadcrumb/WCBreadcrumb.vue';
import WCSearchInput from '@/components/WCSearchInput/WCSearchInput.vue';

export default {
  components: {
    WCStore,
    WCPagination,
    WCSpinner,
    WCBreadcrumb,
    WCSearchInput,
    WCValidationMessage,
  },
  mixins: [FocusMixin], // Mixin for main content focus via routing
  async mounted() {
    try {
      this.loading = true;
      const storesResponse = await axios.get('api/stores');
      this.stores = storesResponse.data.data;

      this.mapService = new MapService();

      if (this.stores.some(store => store.latitude && store.longitude)) {
        this.mapService.load().then(() => {
          const options = {
            zoom: 15,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            gestureHandling: 'greedy',
            center: new google.maps.LatLng(0, 0),
          };
          this.map = new google.maps.Map(this.$refs.mapElement, options);

          // Create markers and build initial map boundaries
          const bounds = new google.maps.LatLngBounds();
          this.stores = this.stores.map(store => {
            if (store.latitude && store.longitude) {
              const position = new google.maps.LatLng(store.latitude, store.longitude);

              bounds.extend(position);
              const marker = new google.maps.Marker({
                map: this.map,
                title: store.name,
                position,
              });

              google.maps.event.addListener(marker, 'click', () => {
                this.selectStore(store);
              });

              return { ...store, marker };
            }

            return store;
          });
          this.map.fitBounds(bounds);

          // Attempt to recenter map around user location
          MapService.geolocate().then(location => {
            this.updateOrigin({
              latitude: location.latitude,
              longitude: location.longitude,
            });
          });
        });
      }
      this.loading = false;
    } catch (error) {
      console.error(error);
      this.loading = false;
    }
  },
  methods: {
    /**
     * Method emitted from SearchInput component to update search string
     */
    updateSearchValue(searchValue) {
      this.searchString = searchValue;
    },
    /*
     * Updates the origin point on the map.
     * Calculates the distance from that point to each store, sorts by that distance,
     * and then rebuilds the map boundaries to include nearby stores.
     */
    updateOrigin(origin) {
      this.mapService.load().then(() => {
        const position = new google.maps.LatLng(origin.latitude, origin.longitude);
        this.map.setCenter(position);
        this.map.setZoom(15);

        // Remove origin marker from the map if it already exists
        if (this.originMarker !== null) {
          this.originMarker.setMap(null);
        }

        this.originMarker = new google.maps.Marker({
          map: this.map,
          title: 'Current Location',
          position,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 4,
            strokeColor: '#0078FF',
            fillColor: '#0078FF',
            fillOpacity: 1,
            strokeOpacity: 0.5,
            strokeWeight: 8,
          },
        });

        // Calculate the distance to each store
        this.stores
          .filter(store => store.marker)
          .forEach(store => {
            const distance = MapService.distance(origin, {
              latitude: store.marker.getPosition().lat(),
              longitude: store.marker.getPosition().lng(),
            });
            this.$set(store, 'distance', distance);
          });

        // Sort stores by distance
        this.stores.sort((a, b) => a.distance - b.distance);

        // Update map view to include stores within 25 miles
        const nearby = this.stores.filter(store => store.distance < 40250);
        if (nearby && nearby.length) {
          const bounds = new google.maps.LatLngBounds(position);
          nearby.forEach(store => bounds.extend(store.marker.getPosition()));
          this.map.fitBounds(bounds);
        }
      });
    },
    /*
     * Attempts to locate and set the search location as the map's origin
     */
    search() {
      if (this.map && this.searchString && !this.$v.searchString.$invalid) {
        this.mapService.load().then(() => {
          this.mapService
            .geocode(this.searchString)
            .then(location => {
              this.updateOrigin(location);
              this.geocodeError = null;
            })
            .catch(e => {
              this.geocodeError = { message: e.message, search: this.searchString };
            });
        });
      }
    },
    selectStore(store) {
      this.selectedStore = store;

      // Jump to correct page
      const index = this.stores.findIndex(s => s.id === store.id);
      if (index >= 0) {
        this.currentPage = Math.floor(index / this.pageSize) + 1;
      }

      if (this.map && store.marker) {
        this.map.panTo(store.marker.getPosition());
      }
    },
    isCurrentStore(store) {
      const current = this.$store.getters.getConfiguration.store;
      return store.id === current.id;
    },
    isHomeStore(store) {
      return store.id === this.getUser?.home;
    },
    isSelected(store) {
      return this.selectedStore !== null && store.id === this.selectedStore.id;
    },
  },
  computed: {
    ...mapGetters('user', ['getUser']),
    storeList() {
      return this.stores.slice(
        (this.currentPage - 1) * this.pageSize,
        this.currentPage * this.pageSize,
      );
    },
    /**
     * To display crumbs data
     */
    storeCrumbs() {
      const breadcrumbs = this.$route.meta.breadcrumbs;
      return breadcrumbs && breadcrumbs.length ? breadcrumbs : [];
    },
  },
  data() {
    return {
      loading: false,
      mapService: {},
      stores: [],
      map: {},
      originMarker: null,
      selectedStore: null,
      searchString: '',
      currentPage: 1,
      pageSize: 10,
      hasFocus: false,
      geocodeError: null,
    };
  },
  validations: {
    searchString: {
      postal,
      maxLength: maxLength(5),
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.select-store-header {
  border-bottom: 3px solid var(--secondary, $secondary);
}

.store-select__section {
  button {
    border: 1px solid var(--gray-400, $gray-400) !important;
    border-right: 0 !important;
  }
}

.store-select__map {
  border: 1px solid var(--gray-500, $gray-500);
}

.wc-store-list-wrapper {
  width: 100%;
}

.wc-map-wrapper {
  width: 100%;
  height: 100%;
}

.search-box {
  .form-control:focus {
    box-shadow: none;
  }
}

.store-select {
  &.store-select-zipcode {
    max-width: rem(260px);
  }

  // Small devices
  @include media-breakpoint-down(md) {
    &__map {
      height: rem(300px);
    }
    &.store-select-zipcode {
      max-width: 100%;
    }
  }

  // Large devices
  @include media-breakpoint-up(lg) {
    &__map {
      height: rem(500px);
    }
  }

  &__store-list {
    margin-bottom: $margin-2;
  }

  &__section {
    margin-bottom: $margin-3;
  }

  &__map {
    display: block;
  }
}
</style>
